import * as EmailValidator from 'email-validator';
import { CellObject, Range, read, utils, WorkBook, WorkSheet, write } from 'xlsx';

import {
  cleanDistributionValue,
  isValidDistributionPhone,
} from '../../shared/utilities/distribution-validation.util';

/**
 * Pure SheetJS helpers that write Distribution List page edits back into the workbook in place.
 *
 * File layout (same as the legacy importer): one sheet per list, headers on the first row of the
 * sheet range, purpose text in `K1`, display name of a renamed/created list in `L1`. Cells are
 * edited in place so columns that the page does not model (`HAL ID`, `BUSINESS UNIT`, `ORDER`,
 * `K1`, ...) and non-list sheets are preserved. SheetJS Community Edition does not write cell
 * styles; values, column widths, row heights, merges and number formats are kept.
 */

export const PRIMARY_SHEET_NAME = 'PRIMARY POINTS OF CONTACT';

const CONTACT_HEADERS = [
  'FIRST NAME',
  'LAST NAME',
  'BUSINESS UNIT',
  'HAL ID',
  'TITLE',
  'PHONE',
  'EMAIL',
] as const;
const ORDER_HEADER = 'ORDER';
/**
 * ORDER written on Primary when a user without any ORDER value joins it.
 * TODO: temporary fix, pending PO confirmation of the final behavior (default value vs. error).
 */
const DEFAULT_PRIMARY_ORDER = 'C1';
const COMPANY_HEADER = 'COMPANY';
const HAL_ID_HEADER = 'HAL ID';
const EXTERNAL_COMPANY = 'External';
const HALLIBURTON_COMPANY = 'Halliburton';
/** Per-sheet flag; never copied between sheets. A blank or missing cell means notifications are on. */
export const NOTIFICATION_HEADER = 'SEND NOTIFICATIONS';
const NOTIFICATION_ON = 'YES';
const NOTIFICATION_OFF = 'NO';
const NOTIFICATION_OFF_VALUES = /^(no|n|false|0)$/i;
/** Columns copied from the user's existing rows when the user is added to another list. */
const COPIED_HEADERS: readonly string[] = [...CONTACT_HEADERS, COMPANY_HEADER, ORDER_HEADER];
const EDITABLE_COLUMNS = {
  email: 'EMAIL',
  phone: 'PHONE',
  title: 'TITLE',
} as const;
/** Display name of a renamed or created list (read by the wizard upload parser, ignored by the backend). */
const TAB_NAME_ADDRESS = 'L1';
const TAB_NAME_COLUMN = 11;
/** Sheet names the backend reads (see `readDistributionList` in the backend). */
const KNOWN_SHEET_NAMES = [
  PRIMARY_SHEET_NAME,
  'GREEN',
  'YELLOW',
  'RED',
  'BLUE',
  'GREY',
  'ORANGE',
  'PEACH',
  'MAROON',
  'BROWN',
  'CYAN',
  'PINK',
  'NAVY',
  'PURPLE',
  'LIME',
];
/** Sheets handed to lists created on the page. */
const FREE_SHEET_SLOTS = ['GREY', 'ORANGE', 'PEACH', 'MAROON', 'BROWN', 'CYAN', 'PINK', 'NAVY', 'LIME'];
/** The backend prefixes every stored blob with `Date.now()_`. */
const UPLOAD_TIMESTAMP_PREFIX = /^(\d{13}_)+/;

export type DistributionWorkbookErrorCode =
  | 'MISSING_SHEET'
  | 'MISSING_HEADER'
  | 'USER_NOT_FOUND'
  | 'DUPLICATE_EMAIL'
  | 'NO_LIST'
  | 'INVALID_PRIMARY_PHONE'
  | 'SHEET_LIMIT'
  | 'VERIFY_FAILED';

export class DistributionWorkbookError extends Error {
  constructor(
    readonly code: DistributionWorkbookErrorCode,
    message: string
  ) {
    super(message);
    this.name = 'DistributionWorkbookError';
  }
}

export type WorkbookEditableField = keyof typeof EDITABLE_COLUMNS;

export interface WorkbookSourceRow {
  /** Sheet the row was edited on. */
  sheetName: string;
  /** Valid HAL ID of the row; only narrows the match when known. */
  halId?: string;
  /** Values the row shows (placeholders such as `N/A` count as blank). */
  name: string;
  title: string;
  phone: string;
}

export interface WorkbookUserEdit {
  /**
   * Email currently stored in the file (the original email when it is being changed). A blank,
   * placeholder or invalid value is never used to find rows; `sourceRow` is used instead.
   */
  lookupEmail: string;
  /**
   * The edited row, required when `lookupEmail` is not usable: exactly one row of its sheet without
   * a usable email must match it, otherwise the edit stops with `USER_NOT_FOUND`.
   */
  sourceRow?: WorkbookSourceRow;
  /**
   * A company change is written to the `COMPANY` column (added when missing); `HAL ID` is never changed.
   * A name is split into `FIRST NAME` / `LAST NAME`; rows that already show the same name are kept.
   */
  changes: Partial<Record<WorkbookEditableField | 'company' | 'name', string>>;
  /** Sheet names of every list (tab) in the file. */
  listSheetNames: string[];
  /** Sheets the user must belong to after the edit. Omit to keep the membership unchanged. */
  targetSheetNames?: string[];
}

export interface WorkbookNewUser {
  name: string;
  company: string;
  title: string;
  email: string;
  phone: string;
  /** HAL ID of a user picked from the directory search. */
  halId?: string;
}

export interface WorkbookUserAdd {
  user: WorkbookNewUser;
  listSheetNames: string[];
  targetSheetNames: string[];
}

export interface WorkbookUsersRemoval {
  /** The only sheet that is changed. */
  sheetName: string;
  emails: string[];
}

export interface WorkbookGroup {
  id: string;
  name: string;
  /** Sheet of a list that exists in the file; empty for a list created on the page. */
  sheetName?: string;
}

export interface WorkbookGroupChanges {
  before: WorkbookGroup[];
  after: WorkbookGroup[];
}

export interface WorkbookEditResult {
  /** Sheets that contain the user after the edit. */
  memberOf: string[];
  addedTo: string[];
  removedFrom: string[];
  /** Only when set: sheets the edit does not touch that already hold the new email (allowed). */
  otherRowSheets?: string[];
}

export interface WorkbookEditExpectation {
  email: string;
  originalEmail?: string;
  listSheetNames: string[];
  expectedSheetNames: string[];
}

export interface WorkbookNotificationEdit {
  /** The only sheet that is changed. */
  sheetName: string;
  email: string;
  enabled: boolean;
}

interface ListSheet {
  name: string;
  sheet: WorkSheet;
  headerRow: number;
  columns: Map<string, number>;
}

export function readDistributionWorkbook(data: ArrayBuffer): WorkBook {
  return read(data, { type: 'array', cellStyles: true, cellNF: true });
}

export function writeDistributionWorkbook(workbook: WorkBook): ArrayBuffer {
  return write(workbook, {
    bookType: 'xlsx',
    type: 'array',
    compression: true,
    cellStyles: true,
  }) as ArrayBuffer;
}

/** Removes the `Date.now()_` prefixes added by previous uploads so names do not grow. */
export function stripUploadTimestamp(fileName: string): string {
  return fileName.replace(UPLOAD_TIMESTAMP_PREFIX, '') || fileName;
}

export function parseNotificationFlag(value: unknown): boolean {
  return !NOTIFICATION_OFF_VALUES.test(String(value ?? '').trim());
}

/**
 * Company of a parsed row: the `COMPANY` cell typed on the page, otherwise `Halliburton` when the
 * row has a HAL ID and `External` when it does not.
 */
export function readCompany(row: object): string {
  const cell = (header: string): string =>
    String(Object.entries(row).find(([key]) => sheetKey(key) === header)?.[1] ?? '').trim();
  return cell(COMPANY_HEADER) || (cell(HAL_ID_HEADER) ? HALLIBURTON_COMPANY : EXTERNAL_COMPANY);
}

/** Reads the `SEND NOTIFICATIONS` value of a parsed row (header matched ignoring case and spaces). */
export function readNotificationFlag(row: object): boolean {
  const entry = Object.entries(row).find(([key]) => sheetKey(key) === NOTIFICATION_HEADER);
  return parseNotificationFlag(entry?.[1]);
}

/**
 * Sets the user's `SEND NOTIFICATIONS` cell on `edit.sheetName` only, adding the column to that
 * sheet when missing. Other sheets keep their own value.
 */
export function applyNotificationEdit(workbook: WorkBook, edit: WorkbookNotificationEdit): void {
  const sheet = getListSheet(workbook, edit.sheetName);
  const rows = findUserRows(sheet, normalizeEmail(edit.email));
  if (!rows.length) {
    throw new DistributionWorkbookError(
      'USER_NOT_FOUND',
      `User "${edit.email}" was not found in "${sheet.name}". Please reload the page.`
    );
  }
  ensureColumn(sheet, NOTIFICATION_HEADER);
  const column = sheet.columns.get(NOTIFICATION_HEADER)!;
  const value = edit.enabled ? NOTIFICATION_ON : NOTIFICATION_OFF;
  rows.forEach(row => setTextCell(sheet.sheet, row, column, value));
}

/** Re-reads the serialized file and checks the user's flag on the edited sheet. */
export function verifyNotificationEdit(data: ArrayBuffer, edit: WorkbookNotificationEdit): void {
  const sheet = getListSheet(readDistributionWorkbook(data), edit.sheetName);
  const column = sheet.columns.get(NOTIFICATION_HEADER);
  const rows = findUserRows(sheet, normalizeEmail(edit.email));
  const isSaved =
    column !== undefined &&
    rows.length > 0 &&
    rows.every(
      row =>
        parseNotificationFlag(sheet.sheet[utils.encode_cell({ r: row, c: column })]?.v) ===
        edit.enabled
    );
  if (!isSaved) {
    throw new DistributionWorkbookError(
      'VERIFY_FAILED',
      `The notification setting of "${edit.email}" was not saved in "${sheet.name}".`
    );
  }
}

/**
 * Mutates `workbook` in place: updates the user's rows, then adds/removes rows so the user
 * belongs exactly to `targetSheetNames`. New rows copy the user's existing cells, including
 * `ORDER`; a user without any ORDER who joins Primary gets `C1`. A list sheet missing from the file is created when the user
 * joins it (skipped otherwise) and missing template columns are added to every sheet that is
 * written. Throws `DistributionWorkbookError` before any change instead of writing a partial or
 * ambiguous result.
 */
export function applyUserEdit(workbook: WorkBook, edit: WorkbookUserEdit): WorkbookEditResult {
  const found = edit.listSheetNames.map(name => findListSheet(workbook, name));
  const existing = found.filter((sheet): sheet is ListSheet => !!sheet);
  const lookupEmail = usableEmail(edit.lookupEmail);
  const existingRows = new Map(
    existing.map(sheet => [
      sheet,
      lookupEmail ? findUserRows(sheet, lookupEmail) : findSourceRows(sheet, edit.sourceRow),
    ])
  );

  const rowCount = [...existingRows.values()].reduce((sum, rows) => sum + rows.length, 0);
  if (!rowCount || (!lookupEmail && rowCount > 1)) {
    throw new DistributionWorkbookError(
      'USER_NOT_FOUND',
      lookupEmail
        ? `User "${edit.lookupEmail}" was not found in the distribution list file. Please reload the page.`
        : `The row without email on "${edit.sourceRow?.sheetName ?? 'its sheet'}" could not be matched to exactly one row of the file. Please reload the page.`
    );
  }

  const targetKeys =
    edit.targetSheetNames && resolveTargetKeys(edit.listSheetNames, edit.targetSheetNames);
  if (targetKeys && !targetKeys.size) {
    throw new DistributionWorkbookError('NO_LIST', 'A user must belong to at least one list.');
  }

  // Duplicates count in the target sheets only; other sheets may hold the same email.
  const newEmail = edit.changes.email === undefined ? lookupEmail : normalizeEmail(edit.changes.email);
  // A user without email is the same person as the rows holding the new email on the target sheets.
  if (!lookupEmail && newEmail && targetKeys) {
    existing.forEach(sheet => {
      if (!existingRows.get(sheet)!.length && targetKeys.has(sheetKey(sheet.name))) {
        existingRows.set(sheet, findUserRows(sheet, newEmail));
      }
    });
  }
  const otherRowSheets: ListSheet[] = [];
  if (newEmail && newEmail !== lookupEmail) {
    existing.forEach(sheet => {
      const own = existingRows.get(sheet)!;
      if (!findUserRows(sheet, newEmail).some(row => !own.includes(row))) {
        return;
      }
      // The sheets selected under List Added (or the user's own sheets when unchanged) are checked.
      const inScope = targetKeys ? targetKeys.has(sheetKey(sheet.name)) : own.length > 0;
      if (!inScope) {
        otherRowSheets.push(sheet);
        return;
      }
      throw new DistributionWorkbookError(
        'DUPLICATE_EMAIL',
        `Email "${edit.changes.email}" already exists in "${sheet.name}".`
      );
    });
  }

  const primaryIndex = edit.listSheetNames.findIndex(isPrimarySheet);
  const primary = found[primaryIndex];
  const joinsPrimary =
    primaryIndex >= 0 &&
    !!targetKeys?.has(PRIMARY_SHEET_NAME) &&
    !(primary && existingRows.get(primary)!.length);

  // A phone typed for a user who is (or becomes) a Primary member must be valid. Without a typed
  // phone nothing is checked: the file keeps the phone it has (bulk edit never types one).
  const staysInPrimary = targetKeys
    ? targetKeys.has(PRIMARY_SHEET_NAME)
    : !!primary && existingRows.get(primary)!.length > 0;
  const typedPhone = edit.changes.phone?.trim();
  if (primaryIndex >= 0 && staysInPrimary && typedPhone && !isValidDistributionPhone(typedPhone)) {
    throw new DistributionWorkbookError(
      'INVALID_PRIMARY_PHONE',
      'A valid phone (+<Dial Code> <Phone Number>) is required for users in the Primary list.'
    );
  }

  const sheets = edit.listSheetNames.flatMap((name, index) => {
    const sheet =
      found[index] ?? (targetKeys?.has(sheetKey(name)) ? createListSheet(workbook, name) : null);
    return sheet ? [sheet] : [];
  });
  const matches = new Map(sheets.map(sheet => [sheet, existingRows.get(sheet) ?? []]));
  matches.forEach((rows, sheet) => {
    if (rows.length || targetKeys?.has(sheetKey(sheet.name))) {
      ensureTemplateColumns(sheet);
    }
  });

  const company = edit.changes.company?.trim();
  if (company) {
    matches.forEach((rows, sheet) => {
      if (rows.length || targetKeys?.has(sheetKey(sheet.name))) {
        // Rows appended below copy the COMPANY cell only when their sheet has the column.
        ensureColumn(sheet, COMPANY_HEADER);
      }
      rows.forEach(row => setTextCell(sheet.sheet, row, sheet.columns.get(COMPANY_HEADER)!, company));
    });
  }
  matches.forEach((rows, sheet) => rows.forEach(row => writeChanges(sheet, row, edit.changes)));

  const sourceCells = snapshotUser(matches);
  // TODO: temporary fix, pending PO confirmation (previously threw MISSING_ORDER).
  if (joinsPrimary && !sourceCells.has(ORDER_HEADER)) {
    sourceCells.set(ORDER_HEADER, { t: 's', v: DEFAULT_PRIMARY_ORDER });
  }
  const result: WorkbookEditResult = { memberOf: [], addedTo: [], removedFrom: [] };

  sheets.forEach(sheet => {
    const rows = matches.get(sheet)!;
    const isMember = targetKeys ? targetKeys.has(sheetKey(sheet.name)) : rows.length > 0;
    if (rows.length && !isMember) {
      [...rows].reverse().forEach(row => deleteRow(sheet.sheet, row));
      result.removedFrom.push(sheet.name);
    } else if (!rows.length && isMember) {
      appendRow(sheet, sourceCells);
      result.addedTo.push(sheet.name);
    }
    if (isMember) {
      result.memberOf.push(sheet.name);
    }
  });

  if (otherRowSheets.length) {
    result.otherRowSheets = otherRowSheets.map(sheet => sheet.name);
  }
  return result;
}

/**
 * Re-reads the serialized file and checks the user's list membership; missing sheets hold no rows.
 * A user without a usable email cannot be searched by email, so only the old email check applies.
 */
export function verifyUserEdit(data: ArrayBuffer, expectation: WorkbookEditExpectation): void {
  const workbook = readDistributionWorkbook(data);
  const sheets = expectation.listSheetNames
    .map(name => findListSheet(workbook, name))
    .filter((sheet): sheet is ListSheet => !!sheet);
  const email = normalizeEmail(cleanDistributionValue(expectation.email));
  const expected = [...new Set(expectation.expectedSheetNames.map(sheetKey))].sort();
  const actual = sheets
    .filter(sheet => findUserRows(sheet, email).length)
    .map(sheet => sheetKey(sheet.name))
    .sort();

  if (email && expected.join('|') !== actual.join('|')) {
    throw new DistributionWorkbookError(
      'VERIFY_FAILED',
      `Expected "${expectation.email}" in [${expected.join(', ')}] but found it in [${actual.join(', ')}].`
    );
  }

  const original = usableEmail(expectation.originalEmail);
  if (original && original !== email && sheets.some(sheet => findUserRows(sheet, original).length)) {
    throw new DistributionWorkbookError(
      'VERIFY_FAILED',
      `The previous email "${expectation.originalEmail}" is still present in the file.`
    );
  }
}

/**
 * Appends a user that is not in the file yet to every target sheet. On Primary the ORDER is
 * `E<n>` (has HAL ID) or `C<n>` (external), `n` being the next free number of that prefix.
 * The typed company is written to `COMPANY`. A target list sheet missing from the file is created
 * and missing template columns are added to the target sheets.
 */
export function applyUserAdd(workbook: WorkBook, add: WorkbookUserAdd): void {
  const found = add.listSheetNames.map(name => findListSheet(workbook, name));
  const existing = found.filter((sheet): sheet is ListSheet => !!sheet);
  const email = normalizeEmail(add.user.email);
  if (!email) {
    throw new DistributionWorkbookError('USER_NOT_FOUND', 'A user without email cannot be saved to the file.');
  }
  if (existing.some(sheet => findUserRows(sheet, email).length)) {
    throw new DistributionWorkbookError(
      'DUPLICATE_EMAIL',
      `Email "${add.user.email}" already exists in the distribution list file.`
    );
  }
  const targetKeys = resolveTargetKeys(add.listSheetNames, add.targetSheetNames);
  if (!targetKeys.size) {
    throw new DistributionWorkbookError('NO_LIST', 'A user must belong to at least one list.');
  }

  const company = add.user.company.trim();
  const halId = (add.user.halId ?? '').trim();
  const [firstName, lastName] = splitName(add.user.name);
  const values: [string, string][] = [
    ['FIRST NAME', firstName],
    ['LAST NAME', lastName],
    [HAL_ID_HEADER, halId],
    ['TITLE', add.user.title],
    ['PHONE', add.user.phone],
    ['EMAIL', add.user.email],
  ];
  const cells = new Map<string, CellObject>();
  values.forEach(([header, value]) => {
    if (value.trim()) {
      cells.set(header, { t: 's', v: value.trim() });
    }
  });
  const order = nextOrder(existing, halId ? 'E' : 'C');

  add.listSheetNames
    .map((name, index) =>
      targetKeys.has(sheetKey(name)) ? (found[index] ?? createListSheet(workbook, name)) : null
    )
    .filter((sheet): sheet is ListSheet => !!sheet)
    .forEach(sheet => {
      ensureTemplateColumns(sheet);
      const rowCells = new Map(cells);
      if (company) {
        ensureColumn(sheet, COMPANY_HEADER);
        rowCells.set(COMPANY_HEADER, { t: 's', v: company });
      }
      if (isPrimarySheet(sheet.name)) {
        rowCells.set(ORDER_HEADER, { t: 's', v: order });
      }
      appendRow(sheet, rowCells);
    });
}

/** Removes the users' rows from one sheet only; their rows on other sheets are kept. */
export function applyUsersRemoval(workbook: WorkBook, removal: WorkbookUsersRemoval): void {
  const sheet = getListSheet(workbook, removal.sheetName);
  const rows = removal.emails.flatMap(email => {
    const found = findUserRows(sheet, normalizeEmail(email));
    if (!found.length) {
      throw new DistributionWorkbookError(
        'USER_NOT_FOUND',
        `User "${email}" was not found in "${sheet.name}". Please reload the page.`
      );
    }
    return found;
  });
  [...new Set(rows)].sort((a, b) => b - a).forEach(row => deleteRow(sheet.sheet, row));
}

export function verifyUsersRemoval(data: ArrayBuffer, removal: WorkbookUsersRemoval): void {
  const sheet = getListSheet(readDistributionWorkbook(data), removal.sheetName);
  const remaining = removal.emails.filter(email => findUserRows(sheet, normalizeEmail(email)).length);
  if (remaining.length) {
    throw new DistributionWorkbookError(
      'VERIFY_FAILED',
      `[${remaining.join(', ')}] are still present in "${sheet.name}".`
    );
  }
}

/**
 * Applies the Customize table result: removes the sheets of deleted lists, creates a sheet for
 * every new list (a free sheet name the backend reads) and stores renamed list names in `L1`.
 * Returns the sheet name of every list in `after`. Colors are not stored in the file.
 */
export function applyGroupChanges(
  workbook: WorkBook,
  changes: WorkbookGroupChanges
): Map<string, string> {
  const afterIds = new Set(changes.after.map(group => group.id));
  const sheetNames = new Map<string, string>();
  changes.before.forEach(group => {
    const actual = group.sheetName && findSheetName(workbook, group.sheetName);
    if (actual && afterIds.has(group.id)) {
      sheetNames.set(group.id, actual);
    }
  });

  const used = new Set(workbook.SheetNames.map(sheetKey));
  changes.before
    .filter(group => !afterIds.has(group.id) && group.sheetName)
    .forEach(group => used.delete(sheetKey(group.sheetName!)));
  const created: string[] = [];
  changes.after
    .filter(group => !sheetNames.has(group.id))
    .forEach(group => {
      const preferred = sheetKey(group.id) === 'PRIMARY' ? PRIMARY_SHEET_NAME : sheetKey(group.id);
      const name = [preferred, ...FREE_SHEET_SLOTS].find(
        slot => KNOWN_SHEET_NAMES.includes(slot) && !used.has(slot)
      );
      if (!name) {
        throw new DistributionWorkbookError(
          'SHEET_LIMIT',
          'There are more lists than the distribution list file can hold. Delete a list and try again.'
        );
      }
      used.add(name);
      sheetNames.set(group.id, name);
      created.push(name);
    });

  changes.before
    .filter(group => !afterIds.has(group.id) && group.sheetName)
    .forEach(group => removeSheet(workbook, findSheetName(workbook, group.sheetName!)));
  created.forEach(name =>
    utils.book_append_sheet(
      workbook,
      utils.aoa_to_sheet([isPrimarySheet(name) ? [...CONTACT_HEADERS, ORDER_HEADER] : [...CONTACT_HEADERS]]),
      name
    )
  );
  changes.after.forEach(group => {
    const name = sheetNames.get(group.id)!;
    if (!isPrimarySheet(name)) {
      applyTabName(workbook.Sheets[name], group.name, name);
    }
  });
  return sheetNames;
}

function findSheetName(workbook: WorkBook, name: string): string | undefined {
  return workbook.SheetNames.find(sheetName => sheetKey(sheetName) === sheetKey(name));
}

function removeSheet(workbook: WorkBook, name: string | undefined): void {
  if (!name) {
    return;
  }
  workbook.SheetNames = workbook.SheetNames.filter(sheetName => sheetName !== name);
  delete workbook.Sheets[name];
  if (workbook.Workbook?.Sheets) {
    workbook.Workbook.Sheets = workbook.Workbook.Sheets.filter(sheet => sheet.name !== name);
  }
}

/** `L1` holds the list name only when it differs from the sheet's default label. */
function applyTabName(sheet: WorkSheet, groupName: string, sheetName: string): void {
  const defaultLabel = sheetName.charAt(0).toUpperCase() + sheetName.slice(1).toLowerCase();
  const name = groupName.trim();
  if (name && name !== defaultLabel) {
    sheet[TAB_NAME_ADDRESS] = { t: 's', v: name };
    const range = sheetRange(sheet);
    range.e.c = Math.max(range.e.c, TAB_NAME_COLUMN);
    sheet['!ref'] = utils.encode_range(range);
  } else {
    delete sheet[TAB_NAME_ADDRESS];
  }
}

function splitName(name: string): [string, string] {
  const [first = '', ...rest] = name.trim().split(/\s+/).filter(Boolean);
  return [first, rest.join(' ')];
}

/** Next `E<n>` / `C<n>` ORDER value, scanning the ORDER column of every list sheet. */
function nextOrder(sheets: ListSheet[], prefix: 'E' | 'C'): string {
  const pattern = new RegExp(`^${prefix}(\\d+)`, 'i');
  let max = 0;
  sheets.forEach(sheet => {
    const column = sheet.columns.get(ORDER_HEADER);
    if (column === undefined) {
      return;
    }
    for (let r = sheet.headerRow + 1; r <= sheetRange(sheet.sheet).e.r; r++) {
      const match = pattern.exec(String(sheet.sheet[utils.encode_cell({ r, c: column })]?.v ?? '').trim());
      if (match) {
        max = Math.max(max, Number(match[1]));
      }
    }
  });
  return `${prefix}${max + 1}`;
}

/** Strict lookup: the sheet must exist and have every template column. */
function getListSheet(workbook: WorkBook, name: string): ListSheet {
  const sheet = findListSheet(workbook, name);
  if (!sheet) {
    throw new DistributionWorkbookError('MISSING_SHEET', `Sheet "${name}" was not found in the file.`);
  }
  const missing = templateHeaders(sheet.name).filter(header => !sheet.columns.has(header));
  if (missing.length) {
    throw new DistributionWorkbookError(
      'MISSING_HEADER',
      `Sheet "${sheet.name}" is missing column(s): ${missing.join(', ')}.`
    );
  }
  return sheet;
}

/** Lenient lookup: null when the sheet is missing; the columns are read as they are. */
function findListSheet(workbook: WorkBook, name: string): ListSheet | null {
  const actualName = findSheetName(workbook, name);
  if (!actualName) {
    return null;
  }
  const sheet = workbook.Sheets[actualName];
  const range = sheet['!ref'] ? utils.decode_range(sheet['!ref']) : null;
  const columns = new Map<string, number>();
  if (range) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const header = String(sheet[utils.encode_cell({ r: range.s.r, c })]?.v ?? '')
        .trim()
        .toUpperCase();
      if (header && !columns.has(header)) {
        columns.set(header, c);
      }
    }
  }
  return { name: actualName, sheet, headerRow: range?.s.r ?? 0, columns };
}

/** Adds a list sheet that is missing from the file, with the template headers only. */
function createListSheet(workbook: WorkBook, name: string): ListSheet {
  const sheetName = sheetKey(name);
  const headers = templateHeaders(sheetName);
  utils.book_append_sheet(workbook, utils.aoa_to_sheet([headers]), sheetName);
  return findListSheet(workbook, sheetName)!;
}

/** Appends the template columns a sheet is missing so rows can be written into it. */
function ensureTemplateColumns(sheet: ListSheet): void {
  if (!sheet.sheet['!ref']) {
    templateHeaders(sheet.name).forEach((header, column) => {
      setTextCell(sheet.sheet, 0, column, header);
      sheet.columns.set(header, column);
    });
    sheet.sheet['!ref'] = utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: 0, c: sheet.columns.size - 1 },
    });
    return;
  }
  templateHeaders(sheet.name).forEach(header => ensureColumn(sheet, header));
}

function templateHeaders(sheetName: string): string[] {
  return isPrimarySheet(sheetName) ? [...CONTACT_HEADERS, ORDER_HEADER] : [...CONTACT_HEADERS];
}

/** `listSheetNames` are the page's lists; a target outside them is rejected. */
function resolveTargetKeys(listSheetNames: string[], targetSheetNames: string[]): Set<string> {
  const known = new Set(listSheetNames.map(sheetKey));
  const keys = new Set(targetSheetNames.map(sheetKey));
  const unknown = [...keys].filter(key => !known.has(key));
  if (unknown.length) {
    throw new DistributionWorkbookError(
      'MISSING_SHEET',
      `List(s) not found in the file: ${unknown.join(', ')}.`
    );
  }
  return keys;
}

function findUserRows(sheet: ListSheet, email: string): number[] {
  const emailColumn = sheet.columns.get('EMAIL');
  if (emailColumn === undefined || !sheet.sheet['!ref']) {
    return [];
  }
  const lastRow = sheetRange(sheet.sheet).e.r;
  const rows: number[] = [];
  for (let r = sheet.headerRow + 1; r <= lastRow; r++) {
    if (normalizeEmail(sheet.sheet[utils.encode_cell({ r, c: emailColumn })]?.v) === email) {
      rows.push(r);
    }
  }
  return rows;
}

/** The email as a lookup key; blank, placeholder (`N/A`) and invalid values give `''` and never match a row. */
function usableEmail(value: unknown): string {
  const email = normalizeEmail(cleanDistributionValue(value));
  return email && EmailValidator.validate(email) ? email : '';
}

function cellText(sheet: ListSheet, row: number, header: string): string {
  const column = sheet.columns.get(header);
  return column === undefined
    ? ''
    : String(sheet.sheet[utils.encode_cell({ r: row, c: column })]?.v ?? '').trim();
}

/** Compares names word by word, ignoring case and the `N/A` placeholders the page shows for a missing part. */
function nameKey(parts: string[]): string {
  return parts
    .flatMap(part => part.split(/\s+/))
    .map(cleanDistributionValue)
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/**
 * Rows of the source row's sheet that have no usable email and show the same Name, Title and
 * Phone (and the same HAL ID when it is known). Other sheets are never searched.
 */
function findSourceRows(sheet: ListSheet, source: WorkbookSourceRow | undefined): number[] {
  if (!source || sheetKey(source.sheetName) !== sheetKey(sheet.name) || !sheet.sheet['!ref']) {
    return [];
  }
  const halId = cleanDistributionValue(source.halId).toLowerCase();
  const same = (a: string, b: string): boolean =>
    cleanDistributionValue(a).toLowerCase() === cleanDistributionValue(b).toLowerCase();
  const rows: number[] = [];
  for (let r = sheet.headerRow + 1; r <= sheetRange(sheet.sheet).e.r; r++) {
    const isSource =
      !usableEmail(cellText(sheet, r, 'EMAIL')) &&
      (!halId || same(cellText(sheet, r, HAL_ID_HEADER), halId)) &&
      nameKey([cellText(sheet, r, 'FIRST NAME'), cellText(sheet, r, 'LAST NAME')]) ===
        nameKey([source.name]) &&
      same(cellText(sheet, r, 'TITLE'), source.title) &&
      same(cellText(sheet, r, 'PHONE'), source.phone);
    if (isSource) {
      rows.push(r);
    }
  }
  return rows;
}

function writeChanges(
  sheet: ListSheet,
  row: number,
  changes: WorkbookUserEdit['changes']
): void {
  (Object.keys(EDITABLE_COLUMNS) as WorkbookEditableField[]).forEach(field => {
    const value = changes[field];
    if (value !== undefined) {
      setTextCell(sheet.sheet, row, sheet.columns.get(EDITABLE_COLUMNS[field])!, value.trim());
    }
  });
  if (changes.name?.trim()) {
    writeName(sheet, row, changes.name);
  }
}

/** Splits the name over the name columns unless the row already shows it (keeps hand-split names). */
function writeName(sheet: ListSheet, row: number, name: string): void {
  const first = sheet.columns.get('FIRST NAME');
  const last = sheet.columns.get('LAST NAME');
  if (first === undefined || last === undefined) {
    return;
  }
  const cellText = (column: number): string =>
    String(sheet.sheet[utils.encode_cell({ r: row, c: column })]?.v ?? '').trim();
  const [firstName, lastName] = splitName(name);
  const shown = [cellText(first), cellText(last)].filter(Boolean).join(' ').replace(/\s+/g, ' ');
  if (shown === `${firstName} ${lastName}`.trim()) {
    return;
  }
  setTextCell(sheet.sheet, row, first, firstName);
  setTextCell(sheet.sheet, row, last, lastName);
}

/** First non-empty value per copied column across all of the user's rows. */
function snapshotUser(matches: Map<ListSheet, number[]>): Map<string, CellObject> {
  const cells = new Map<string, CellObject>();
  COPIED_HEADERS.forEach(header => {
    for (const [sheet, rows] of matches) {
      const column = sheet.columns.get(header);
      const cell = rows.length && column !== undefined
        ? sheet.sheet[utils.encode_cell({ r: rows[0], c: column })]
        : undefined;
      if (cell && String(cell.v ?? '').trim()) {
        cells.set(header, { ...cell });
        return;
      }
    }
  });
  return cells;
}

function appendRow(sheet: ListSheet, sourceCells: Map<string, CellObject>): void {
  const range = sheetRange(sheet.sheet);
  const row = range.e.r + 1;
  sheet.columns.forEach((column, header) => {
    const cell = sourceCells.get(header);
    if (cell) {
      sheet.sheet[utils.encode_cell({ r: row, c: column })] = { ...cell };
    }
  });
  range.e.r = row;
  sheet.sheet['!ref'] = utils.encode_range(range);
}

/** Appends a header after the last used column (never `K1` or the `L1` list name) when missing. */
function ensureColumn(sheet: ListSheet, header: string): void {
  if (sheet.columns.has(header)) {
    return;
  }
  const range = sheetRange(sheet.sheet);
  const next = range.e.c + 1;
  const column = next === TAB_NAME_COLUMN ? next + 1 : next;
  setTextCell(sheet.sheet, sheet.headerRow, column, header);
  sheet.columns.set(header, column);
  range.e.c = column;
  sheet.sheet['!ref'] = utils.encode_range(range);
}

/** Removes a row and shifts the rows below it up, keeping row heights and merges aligned. */
function deleteRow(sheet: WorkSheet, row: number): void {
  const range = sheetRange(sheet);
  for (let r = row; r < range.e.r; r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const below = sheet[utils.encode_cell({ r: r + 1, c })];
      const target = utils.encode_cell({ r, c });
      if (below) {
        sheet[target] = below;
      } else {
        delete sheet[target];
      }
    }
  }
  for (let c = range.s.c; c <= range.e.c; c++) {
    delete sheet[utils.encode_cell({ r: range.e.r, c })];
  }
  range.e.r -= 1;
  sheet['!ref'] = utils.encode_range(range);
  sheet['!rows']?.splice(row, 1);
  if (sheet['!merges']) {
    sheet['!merges'] = shiftMergesUp(sheet['!merges'], row);
  }
}

function shiftMergesUp(merges: Range[], row: number): Range[] {
  return merges
    .filter(merge => !(merge.s.r === row && merge.e.r === row))
    .map(merge => {
      if (merge.s.r > row) {
        return { s: { ...merge.s, r: merge.s.r - 1 }, e: { ...merge.e, r: merge.e.r - 1 } };
      }
      if (merge.e.r >= row) {
        return { s: { ...merge.s }, e: { ...merge.e, r: merge.e.r - 1 } };
      }
      return merge;
    });
}

/** Writes a string cell, keeping the previous number format; an empty value clears the cell. */
function setTextCell(sheet: WorkSheet, row: number, column: number, value: string): void {
  const address = utils.encode_cell({ r: row, c: column });
  if (!value) {
    delete sheet[address];
    return;
  }
  const previous = sheet[address] as CellObject | undefined;
  const cell: CellObject = { t: 's', v: value };
  if (previous?.z !== undefined) {
    cell.z = previous.z;
  }
  if (previous?.s !== undefined) {
    cell.s = previous.s;
  }
  sheet[address] = cell;
}

function sheetRange(sheet: WorkSheet): Range {
  return utils.decode_range(sheet['!ref']!);
}

function isPrimarySheet(name: string): boolean {
  return sheetKey(name) === PRIMARY_SHEET_NAME;
}

function sheetKey(name: string): string {
  return name.trim().toUpperCase();
}

function normalizeEmail(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}
